import './ArenaVotePage.css';

import { ReactNode, useCallback, useEffect, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { ArenaPanel } from '@/components/ArenaPanel';
import { Logo, LogoAnimated } from '@/components/Logo';
import {
  PromptDisplay,
  type VoteChoice,
  VoteControls,
} from '@/components/voting';
import { useMatchNavigation } from '@/hooks/useMatchNavigation';

import { useArenaPolling } from './hooks/useArenaPolling';
import { useMatchResults } from './hooks/useMatchResults';
import { useVoteSubmission } from './hooks/useVoteSubmission';
import type { MatchPair } from './types';

export default function VotePageArena() {
  const { id: runId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Parse source from URL params (for distinguishing seed vs arena)
  const source = searchParams.get('source') as 'arena' | 'seed' | null;
  const isSeedSource = source === 'seed';

  // Local vote storage - Map<matchIndex, VoteChoice>
  const [pendingVotes, setPendingVotes] = useState<Map<number, VoteChoice>>(
    new Map()
  );

  // Toolbar controls state - rendered in panel headers
  const [toolbarA, setToolbarA] = useState<ReactNode>(null);
  const [toolbarB, setToolbarB] = useState<ReactNode>(null);

  // Track completion animation state
  const [justCompletedIdx, setJustCompletedIdx] = useState<number | null>(null);
  const [prevReadyModels, setPrevReadyModels] = useState(0);

  // Use arena polling hook to manage run loading and match generation
  const {
    run,
    readyMatches,
    loading,
    error,
    setError,
    resultLoadingProgress,
    effectiveTotalMatches,
  } = useArenaPolling(runId);

  // Use match navigation hook
  const navigation = useMatchNavigation<MatchPair>(
    readyMatches,
    0,
    effectiveTotalMatches
  );
  const {
    currentIndex,
    currentMatch,
    next,
    previous,
    canGoPrevious,
    canGoNext,
    markVoted,
    isAllVoted,
    votedCount,
    goTo,
    votedIndices,
  } = navigation;

  // Use match results hook to fetch current match data
  const { currentMatchResults } = useMatchResults(currentMatch);

  // Use vote submission hook
  const {
    currentSelection,
    voting,
    submitting,
    error: voteError,
    setError: setVoteError,
    handleVote,
    handleNext,
    submitAllVotes,
  } = useVoteSubmission({
    runId,
    readyMatches,
    effectiveTotalMatches,
    currentIndex,
    currentMatch,
    pendingVotes,
    setPendingVotes,
    markVoted,
    next,
    goTo,
    canGoNext,
  });

  // Combine errors from different sources
  const displayError = error || voteError;

  // Back URL is always home
  const backUrl = '/';

  // Detect when a model completes for completion animation
  const { readyModels } = resultLoadingProgress;
  useEffect(() => {
    if (readyModels > prevReadyModels) {
      // Both setState calls are in async callbacks (microtask + timeout)
      queueMicrotask(() => {
        setJustCompletedIdx(readyModels - 1);
        setPrevReadyModels(readyModels);
      });
      const timer = setTimeout(() => setJustCompletedIdx(null), 300);
      return () => clearTimeout(timer);
    }
  }, [readyModels, prevReadyModels]);

  // Panel click handler for selection (must be before early returns)
  const handlePanelClick = useCallback(
    (side: 'a' | 'b') => {
      if (!currentMatch || voting) return;
      handleVote(side);
    },
    [currentMatch, voting, handleVote]
  );

  if (loading) {
    const { readyModels, totalModels } = resultLoadingProgress;

    return (
      <div
        className="min-h-screen flex items-center justify-center relative overflow-hidden"
        style={{
          background:
            'linear-gradient(165deg, #9A2B3C 0%, #8B2332 30%, #6B1A27 70%, #4A1219 100%)',
        }}
      >
        {/* Grid pattern */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.04]"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <pattern
              id="loadingGrid"
              width="60"
              height="60"
              patternUnits="userSpaceOnUse"
            >
              <rect
                width="60"
                height="60"
                fill="none"
                stroke="white"
                strokeWidth="0.5"
              />
              <line
                x1="20"
                y1="0"
                x2="20"
                y2="60"
                stroke="white"
                strokeWidth="0.3"
              />
              <line
                x1="40"
                y1="0"
                x2="40"
                y2="60"
                stroke="white"
                strokeWidth="0.3"
              />
              <line
                x1="0"
                y1="15"
                x2="60"
                y2="15"
                stroke="white"
                strokeWidth="0.2"
              />
              <line
                x1="0"
                y1="30"
                x2="60"
                y2="30"
                stroke="white"
                strokeWidth="0.2"
              />
              <line
                x1="0"
                y1="45"
                x2="60"
                y2="45"
                stroke="white"
                strokeWidth="0.2"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#loadingGrid)" />
        </svg>

        {/* Flowing curves */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 1400 800"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
        >
          <path
            className="wave-1"
            d="M-200 200 Q 200 120, 500 200 Q 800 280, 1100 180 Q 1400 80, 1600 160"
            stroke="white"
            strokeWidth="1"
            strokeOpacity="0.08"
            fill="none"
          />
          <path
            className="wave-2"
            d="M-200 400 Q 250 320, 550 420 Q 850 520, 1150 400 Q 1450 280, 1600 380"
            stroke="white"
            strokeWidth="1.5"
            strokeOpacity="0.1"
            fill="none"
          />
          <path
            className="wave-3"
            d="M-200 600 Q 300 520, 600 620 Q 900 720, 1200 600 Q 1500 480, 1600 560"
            stroke="white"
            strokeWidth="1"
            strokeOpacity="0.06"
            fill="none"
          />
        </svg>

        {/* Main content */}
        <div className="relative z-10 text-center px-6">
          {/* Logo */}
          <div className="flex justify-center mb-10 fade-up">
            <LogoAnimated size={64} variant="light" />
          </div>

          {/* Title - conditional on progress */}
          <h2
            className="font-serif text-4xl md:text-5xl font-medium mb-3 fade-up"
            style={{ color: '#FFFFFF', animationDelay: '100ms' }}
          >
            {run?.status === 'running'
              ? readyModels === 0
                ? 'Generating Results'
                : 'Building Spreadsheets'
              : 'Preparing Arena'}
          </h2>

          {/* Cell-based progress visualization - one column per model */}
          {run?.status === 'running' && totalModels > 0 && (
            <div className="fade-up" style={{ animationDelay: '200ms' }}>
              {/* Model columns - each column represents a model in the candidate matches */}
              <div className="flex justify-center gap-3 mb-6">
                {Array.from({ length: totalModels }).map((_, modelIdx) => {
                  const isComplete = modelIdx < readyModels;
                  const isCurrentlyGenerating = modelIdx === readyModels;
                  const isJustCompleted = modelIdx === justCompletedIdx;

                  return (
                    <div key={modelIdx} className="flex flex-col gap-1">
                      {/* 4 cells per column (like the logo) */}
                      {[0, 1, 2, 3].map(cellIdx => {
                        // Wave delay for the currently generating column only
                        const waveDelay = cellIdx * 100;
                        // Completion animation delay (staggered from bottom)
                        const completeDelay = (3 - cellIdx) * 50;

                        return (
                          <div
                            // Key includes readyModels to restart wave animation
                            key={`${cellIdx}-${isCurrentlyGenerating ? readyModels : 0}`}
                            className={`w-3 h-4 rounded-sm ${isCurrentlyGenerating ? 'cell-wave' : ''} ${isJustCompleted ? 'cell-complete' : ''}`}
                            style={{
                              background: isComplete
                                ? `rgba(255,255,255,${1 - cellIdx * 0.2})`
                                : 'rgba(255,255,255,1)',
                              opacity:
                                isComplete && !isJustCompleted
                                  ? 1
                                  : isCurrentlyGenerating
                                    ? undefined
                                    : 0.15,
                              animationDelay: isCurrentlyGenerating
                                ? `${waveDelay}ms`
                                : isJustCompleted
                                  ? `${completeDelay}ms`
                                  : undefined,
                            }}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Progress count */}
              <p
                className="text-sm tabular-nums"
                style={{ color: 'rgba(255,255,255,0.5)' }}
              >
                {readyModels} of {totalModels} models ready
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (displayError) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: '#F5F0EB' }}
      >
        <div className="text-center max-w-sm fade-up">
          <div className="flex justify-center mb-6">
            <Logo size={48} variant="default" />
          </div>
          <p className="text-base mb-6" style={{ color: '#8B2332' }}>
            {displayError}
          </p>
          <Link to={backUrl}>
            <button
              className="px-5 py-2.5 rounded-lg text-sm font-medium transition-opacity hover:opacity-90"
              style={{ background: '#8B2332', color: '#FFF' }}
            >
              Back to Arena
            </button>
          </Link>
        </div>
      </div>
    );
  }

  // Check if user has voted on all available matches but more are expected
  const allAvailableVoted =
    votedCount === readyMatches.length && readyMatches.length > 0;
  const moreMatchesExpected =
    readyMatches.length < effectiveTotalMatches &&
    run?.status !== 'completed' &&
    run?.status !== 'failed';
  const shouldShowWaitingState = allAvailableVoted && moreMatchesExpected;

  if (shouldShowWaitingState) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          {/* Title */}
          <h2 className="text-[2rem] font-medium text-text text-center mb-3">
            Waiting for Next Match
          </h2>

          {/* Progress info */}
          <div className="text-center mb-6">
            <p className="text-lg text-text-light mb-2">
              You've completed {votedCount} of {effectiveTotalMatches} matches
            </p>
            <p className="text-sm text-text-light">
              {effectiveTotalMatches - readyMatches.length} more{' '}
              {effectiveTotalMatches - readyMatches.length === 1
                ? 'match'
                : 'matches'}{' '}
              still loading...
            </p>
          </div>

          {/* Progress indicator */}
          <div className="bg-[#F5F3F0] rounded-lg p-6 mb-6">
            <div className="flex justify-center items-center gap-3 mb-4">
              <div className="text-2xl font-bold text-accent">
                {readyMatches.length}
              </div>
              <div className="text-text-light">/</div>
              <div className="text-2xl font-bold text-text">
                {effectiveTotalMatches}
              </div>
            </div>
            <p className="text-center text-sm text-text-light">
              Matches ready for voting
            </p>
          </div>

          {/* Pulsing dots animation */}
          <div className="flex justify-center items-center gap-2">
            <div
              className="w-2 h-2 bg-accent rounded-full animate-pulse"
              style={{ animationDelay: '0ms' }}
            />
            <div
              className="w-2 h-2 bg-accent rounded-full animate-pulse"
              style={{ animationDelay: '150ms' }}
            />
            <div
              className="w-2 h-2 bg-accent rounded-full animate-pulse"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (readyMatches.length === 0) {
    return (
      <div className="text-center p-16">
        <h2>No Matches Available</h2>
        <p className="text-text-light mt-2">
          Not enough valid results to generate matches
        </p>
        <Link
          to={backUrl}
          className="inline-block mt-4 px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent-hover"
        >
          ← Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 flex flex-col"
      style={{ background: '#F5F0EB' }}
    >
      {/* Header - Burgundy with Logo and Progress Dots */}
      <header
        className="flex-shrink-0 px-6 h-14 flex items-center justify-between"
        style={{ background: '#8B2332' }}
      >
        <Link
          to={backUrl}
          className="flex items-center gap-3 hover:opacity-90 transition-opacity"
        >
          <Logo size={22} variant="light" />
          <span className="font-serif text-white text-lg">
            Spreadsheet Arena
          </span>
        </Link>

        <div className="flex items-center gap-5">
          {/* Progress dots inline in header */}
          <div className="flex gap-1.5">
            {Array.from({ length: effectiveTotalMatches }).map((_, idx) => {
              const isVoted = votedIndices.has(idx);
              const isCurrent = idx === currentIndex;
              const isAvailable = idx < readyMatches.length;

              return (
                <button
                  key={idx}
                  onClick={() => (isAvailable ? goTo(idx) : null)}
                  disabled={!isAvailable}
                  className="transition-all duration-200"
                  style={{
                    width: isCurrent ? '10px' : '8px',
                    height: isCurrent ? '10px' : '8px',
                    borderRadius: '50%',
                    background: isVoted
                      ? 'rgba(255,255,255,1)'
                      : isAvailable
                        ? 'rgba(255,255,255,0.4)'
                        : 'rgba(255,255,255,0.15)',
                    cursor: isAvailable ? 'pointer' : 'not-allowed',
                    opacity: isAvailable ? 1 : 0.3,
                  }}
                  title={`Match ${idx + 1}${isVoted ? ' (voted)' : ''}`}
                />
              );
            })}
          </div>

          {/* Loading indicator - only show when more matches could still arrive */}
          {readyMatches.length < effectiveTotalMatches &&
            run?.status !== 'completed' &&
            run?.status !== 'failed' && (
              <div className="text-xs text-white/60 flex items-center gap-1.5">
                <div className="w-1 h-1 bg-white/60 rounded-full animate-pulse" />
                {effectiveTotalMatches - readyMatches.length} more loading
              </div>
            )}

          {/* Match count */}
          <span className="text-white text-sm font-medium">
            Match {currentIndex + 1} of {readyMatches.length}
          </span>
        </div>
      </header>

      {/* Prompt Section */}
      <div className="flex-shrink-0 px-6 py-5">
        {run && <PromptDisplay promptText={run.prompt_text} />}
      </div>

      {/* Panel Area - Click-to-select with visual feedback */}
      <div className="flex-1 min-h-0 p-4 flex gap-3">
        {/* Panel A */}
        <div
          className={`flex-1 flex flex-col rounded-xl cursor-pointer panel-transition overflow-hidden ${
            currentSelection === 'a' ? 'panel-selected' : 'panel-hover'
          }`}
          onClick={() => handlePanelClick('a')}
          style={{ background: '#FFFFFF', border: '1px solid #E8E0D8' }}
        >
          {/* Panel header with selection state and toolbar */}
          <div
            className="h-11 flex items-center justify-between px-4 flex-shrink-0"
            style={{
              background: currentSelection === 'a' ? '#8B2332' : '#F8F6F3',
              color: currentSelection === 'a' ? '#FFF' : '#1A1A1A',
              borderBottom: '1px solid #E8E0D8',
            }}
          >
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">Model A</span>
              {currentSelection === 'a' && (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M13.3337 4L6.00033 11.3333L2.66699 8"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            {/* Toolbar controls from ArenaPanel */}
            <div
              className="flex items-center gap-1.5"
              onClick={e => e.stopPropagation()}
            >
              {toolbarA}
            </div>
          </div>

          {/* Panel content */}
          <div className="flex-1 min-h-0">
            {currentMatchResults?.[0] ? (
              <ArenaPanel
                label="Model A"
                submission={currentMatchResults[0]}
                scrollId="arena-a"
                embedded={true}
                renderToolbar={setToolbarA}
                hideViewModeToggle
                hideDownloadButton
              />
            ) : (
              <div
                className="flex items-center justify-center h-full text-sm"
                style={{ color: '#6B6B6B' }}
              >
                Loading...
              </div>
            )}
          </div>
        </div>

        {/* VS Divider - vertical line with subtle "vs" text */}
        <div className="flex flex-col items-center justify-center flex-shrink-0 py-4">
          <div className="flex-1 w-px" style={{ background: '#D1C9C4' }} />
          <span
            className="py-2 text-xs font-medium"
            style={{ color: '#A09080' }}
          >
            vs
          </span>
          <div className="flex-1 w-px" style={{ background: '#D1C9C4' }} />
        </div>

        {/* Panel B */}
        <div
          className={`flex-1 flex flex-col rounded-xl cursor-pointer panel-transition overflow-hidden ${
            currentSelection === 'b' ? 'panel-selected' : 'panel-hover'
          }`}
          onClick={() => handlePanelClick('b')}
          style={{ background: '#FFFFFF', border: '1px solid #E8E0D8' }}
        >
          {/* Panel header with selection state and toolbar */}
          <div
            className="h-11 flex items-center justify-between px-4 flex-shrink-0"
            style={{
              background: currentSelection === 'b' ? '#8B2332' : '#F8F6F3',
              color: currentSelection === 'b' ? '#FFF' : '#1A1A1A',
              borderBottom: '1px solid #E8E0D8',
            }}
          >
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">Model B</span>
              {currentSelection === 'b' && (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M13.3337 4L6.00033 11.3333L2.66699 8"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            {/* Toolbar controls from ArenaPanel */}
            <div
              className="flex items-center gap-1.5"
              onClick={e => e.stopPropagation()}
            >
              {toolbarB}
            </div>
          </div>

          {/* Panel content */}
          <div className="flex-1 min-h-0">
            {currentMatchResults?.[1] ? (
              <ArenaPanel
                label="Model B"
                submission={currentMatchResults[1]}
                scrollId="arena-b"
                embedded={true}
                renderToolbar={setToolbarB}
                hideViewModeToggle
                hideDownloadButton
              />
            ) : (
              <div
                className="flex items-center justify-center h-full text-sm"
                style={{ color: '#6B6B6B' }}
              >
                Loading...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer Controls - Inline Layout */}
      <footer
        className="flex-shrink-0 px-4 py-3 flex items-center justify-between gap-4"
        style={{
          background: 'var(--color-white)',
          borderTop: '1px solid var(--color-cream-dark)',
        }}
      >
        {/* Previous Button */}
        <button
          onClick={previous}
          disabled={!canGoPrevious}
          className="px-5 py-1 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2"
          style={{
            background: canGoPrevious
              ? 'var(--color-white)'
              : 'var(--color-cream-dark)',
            color: canGoPrevious
              ? 'var(--color-ink)'
              : 'var(--color-slate-light)',
            border: `1px solid ${canGoPrevious ? 'var(--color-cream-dark)' : 'transparent'}`,
            cursor: canGoPrevious ? 'pointer' : 'not-allowed',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M10 12L6 8L10 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Previous
        </button>

        {/* Vote Buttons */}
        {isAllVoted ? (
          <button
            onClick={() => {
              submitAllVotes()
                .then(() => {
                  // Include source param for seed runs
                  const resultsUrl = isSeedSource
                    ? `/arena/${runId}/results?source=seed`
                    : `/arena/${runId}/results`;
                  navigate(resultsUrl);
                })
                .catch(() => {
                  // Error already set
                });
            }}
            disabled={submitting}
            className="px-8 py-1 rounded-lg text-sm font-semibold transition-all duration-200"
            style={{
              background: submitting
                ? 'var(--color-cream-dark)'
                : 'var(--color-meridian)',
              color: 'var(--color-white)',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'Submitting...' : 'Finish & View Results'}
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <VoteControls
              onVote={handleVote}
              disabled={voting}
              voting={voting}
              currentVote={currentSelection}
              autoAdvance={false}
            />
          </div>
        )}

        {/* Next Button */}
        <button
          onClick={handleNext}
          disabled={!currentSelection || isAllVoted}
          className="px-5 py-1 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2"
          style={{
            background:
              currentSelection && !isAllVoted
                ? 'var(--color-meridian)'
                : 'var(--color-cream-dark)',
            color:
              currentSelection && !isAllVoted
                ? 'var(--color-white)'
                : 'var(--color-slate-light)',
            cursor: currentSelection && !isAllVoted ? 'pointer' : 'not-allowed',
          }}
        >
          Next
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 12L10 8L6 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </footer>

      {/* Error Toast */}
      {displayError && (
        <div
          className="fixed bottom-24 left-1/2 -translate-x-1/2 px-5 py-3 rounded-lg shadow-lg max-w-md text-center fade-up"
          style={{ background: '#8B2332', color: '#FFF' }}
        >
          {displayError}
        </div>
      )}
    </div>
  );
}
